import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Modal,
  Row,
  Spin,
} from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import {
  getNoticeDetail,
  postNoticeSurveyReply,
} from "../../../apis/Notice/apis";
import { HttpError } from "../../../network/types";
import { alertError } from "../../../shared/utils";
import { theme } from "../../../theme";
import { CardTitleTypo } from "../Card/index.styled";
import { Check } from "../Icon";
import { ModalBody, ModalConfirmTitle } from "./Modal.styled";
import { ResultModal } from "./ResultModal";
import { OperatinTags } from "./Tags/Operating";

dayjs.extend(isBetween);

interface NoticeDetailFormProps {
  id: number;
}

export const NoticeDetailForm: React.FC<NoticeDetailFormProps> = ({ id }) => {
  const [modal, contextHolder] = Modal.useModal();

  const { data, isLoading, isFetching, refetch, remove } = useQuery(
    ["Notice", id],
    () => getNoticeDetail(+id),
    {
      refetchOnMount: "always",
      onError: (err: HttpError) => {
        alertError(err.message);
      },
    }
  );

  const { mutateAsync, isLoading: isMutateLoading } = useMutation(
    postNoticeSurveyReply
  );

  const handleServey = (id: number, acceptYn: boolean) => {
    mutateAsync({ id, acceptYn })
      .then(() => {
        modal.success({
          title: <ModalConfirmTitle>정비가능항목 변경 완료</ModalConfirmTitle>,
          content: <ResultModal acceptYn={acceptYn} />,
          icon: false,
          centered: true,
          open: true,
          onOk: () => {
            remove();
            refetch();
          },
        });
      })
      .catch((e: any) => {
        alertError(e.message);
      });
  };

  const isOperating = useMemo(() => {
    if (data === undefined) return undefined;

    return dayjs().isBetween(data.startedAt, data.endedAt);
  }, [data]);

  return (
    <Card
      title={<CardTitleTypo>{data?.title || ""}</CardTitleTypo>}
      bodyStyle={{ display: "flex", justifyContent: "center" }}
    >
      <Spin spinning={isLoading || isFetching}>
        <Row gutter={0} style={{ width: 700 }} justify="center">
          <Col span={24}>
            <Descriptions colon={false} column={3}>
              <Descriptions.Item label="시작일">
                {data?.startedAt
                  ? dayjs(data?.startedAt).format("YYYY.MM.DD")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="종료일">
                {data?.endedAt
                  ? dayjs(data?.endedAt).format("YYYY.MM.DD")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="상태">
                <OperatinTags isOperating={isOperating} />
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Divider />
          <Col span={24}>
            <ModalBody dangerouslySetInnerHTML={{ __html: data?.body || "" }} />
          </Col>
          {data?.isSurvey === true && (
            <Col span={24}>
              <Row justify="center" gutter={12}>
                {!data?.noticeHistories?.length && (
                  <>
                    <Col>
                      <Button
                        onClick={() => handleServey(data.id as number, false)}
                        type="default"
                        size="large"
                        loading={isMutateLoading}
                      >
                        동의안함
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() => handleServey(data.id as number, true)}
                        type="primary"
                        size="large"
                        loading={isMutateLoading}
                      >
                        동의하기
                      </Button>
                    </Col>
                  </>
                )}
                {(data?.noticeHistories?.length || 0) > 0 && (
                  <Col>
                    <Button
                      disabled
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                      icon={
                        <Check
                          fill={theme.colors.gray6}
                          width={18}
                          height={18}
                        />
                      }
                      size="large"
                    >
                      응답 완료
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          )}
        </Row>
        {contextHolder}
      </Spin>
    </Card>
  );
};
